import React, { useRef, useState } from 'react';
import { RadioInputQuestion } from '../../common/radioInputQuestion/RadioInputQuestion';
import { InformationPanel, TextParagraph } from '../../common/informationPanel/InformationPanel';
import { TextInput } from '../../common/textInput/TextInput';
import { ButtonContainer } from '../../common/buttonContainer/ButtonContainer';
import { Button, ButtonTheme } from '../../common/button/Button';
import {
  buildXrinvRequest,
  getAuthTokenOrLogOut,
  getMpxnError,
  InventoryCheckReportingRequest,
  isMpxnValid,
  reportingUtils,
  SiteDecommissionReportingRequest,
} from '../../util';
import {
  DecommissionASiteInventoryCheckType,
  DecommissionASiteResultStatus,
  DecommissionASiteResultType,
  DecommissionASiteScreenContext,
  DecommissionASiteScreenType,
  INITIAL_DECOMMISSION_SITE_RESULT,
  useDecommissionASiteInputsContext,
} from '../../../context/DecommissionASiteContext';
import { LoadingScreen } from './LoadingScreen';
import { useChatReasonContext, useHomePageContext } from '../../../context/HomePageContext';
import DecommissionASiteSummary from './DecommissionASiteSummary';
import { DecommissionASiteInProgressScreen } from './DecommissionASiteInProgressScreen';
import { atomProxyCommsCheckClient, atomProxyDecommissionClient, reportingClient } from '../../../api/atomProxyClient';
import { buildPostRequest } from '../../../api/apiClient';
import { XrinvData } from '../../../types/comms-check';
import { DecommissionASiteResultScreen } from './DecommissionASiteResultScreen';
import { startChatSession } from '../../chat/startChatSession';
import { buildChatData } from './util';
import { parseSmetsVersion } from '../../util';
import { S1_FULL_SITE_DECOMMISSION } from '../../chat/Chat';

export default function DecommissionASite() {
  const [showMpxnError, setShowMpxnError] = useState(false);
  const [mpxnErrorMessage, setMpxnErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const { chatReason } = useChatReasonContext();
  const [screen, setScreen] = React.useState<DecommissionASiteScreenType>(DecommissionASiteScreenType.INITIAL);
  const INITIAL_INVENTORY_DATA: DecommissionASiteInventoryCheckType = {
    address: '',
    data: [],
    state: null,
  };
  const [inventoryData, setInventoryData] = React.useState<DecommissionASiteInventoryCheckType>(INITIAL_INVENTORY_DATA);
  const [decommissionSiteResult, setDecommissionSiteResult] =
    React.useState<DecommissionASiteResultType>(INITIAL_DECOMMISSION_SITE_RESULT);

  const { decommissionASiteInputs, setDecommissionASiteInputs } = useDecommissionASiteInputsContext();
  const { homePageState, setHomePageState } = useHomePageContext();

  // Inventory Check variables
  const XrinvXmlInitialTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_INITIAL_TIMEOUT}`, 10);
  const XrinvXmlTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10);
  const XrinvMaxRetries = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_MAX_RETRIES}`, 10);
  let retriesXrinv = 0;

  // Decommission a Site variables
  const decommXmlInitialTimeout = parseInt(`${process.env.REACT_APP_DECOMMISSION_S1_SITE_GET_XML_INITIAL_TIMEOUT}`, 10);
  const decommXmlTimeout = parseInt(`${process.env.REACT_APP_DECOMMISSION_S1_SITE_GET_XML_TIMEOUT}`, 10);
  const decommMaxRetries = parseInt(`${process.env.REACT_APP_DECOMMISSION_S1_SITE_GET_XML_MAX_RETRIES}`, 10);
  let decommissionRetries = 0;
  const isResultSuccess: boolean = decommissionSiteResult.status === DecommissionASiteResultStatus.SUCCESS;

  const inProgress = useRef(false);
  const inventoryReportingData = useRef({
    decommissionJourneyId: crypto.randomUUID(),
    state: '',
    inventoryCheckStartingTimeStamp: new Date(Date.parse('1 Jan, 2000')),
  });

  const decommissionReportingData = useRef({
    state: '',
    chfDeviceId: '',
    siteDecommissionStartingTimeStamp: new Date(Date.parse('1 Jan, 2000')),
  });

  const onsiteErrorMessage = 'You need to be onsite to continue';
  const onsiteRadioQuestion = 'Are you onsite?';
  const onsiteRadioAnswers = [
    {
      answerValue: 'Yes',
      inputName: 'yes',
      radioBtnGroupName: 'onsiteQuestionGroup',
    },
    {
      answerValue: 'No',
      inputName: 'no',
      radioBtnGroupName: 'onsiteQuestionGroup',
    },
  ];
  const onsiteReminderPanelText: TextParagraph[] = [
    {
      text: 'Please do not attempt a decommission if you are not onsite.',
      lineBreak: false,
    },
  ];

  function onsiteRadioQuestionHandler(event: React.ChangeEvent<HTMLInputElement>) {
    setDecommissionASiteInputs({
      ...decommissionASiteInputs,
      onsiteRadioAnswer: event.target.value.trim(),
    });
    setShowError(false);
    if (event.target.value.trim() === 'No') {
      setShowError(true);
    }
  }

  function mpxnTextInputOnChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    setDecommissionASiteInputs({
      ...decommissionASiteInputs,
      mpxn: event.target.value,
    });
    if (!isMpxnValid(event.target.value)) {
      setShowMpxnError(true);
      setMpxnErrorMessage(getMpxnError(event.target.value));
    } else {
      setShowMpxnError(false);
    }
  }

  function canDecommission(): boolean {
    return !showMpxnError && decommissionASiteInputs.mpxn.length > 0 && decommissionASiteInputs.onsiteRadioAnswer === 'Yes';
  }

  function checkDevicesHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: true,
    });
    inventoryReportingData.current.inventoryCheckStartingTimeStamp = new Date();
    sendSingleGenerateXmlXrinv();
    setScreen(DecommissionASiteScreenType.CHECK_DEVICES);
  }

  const readyToStartCommsCheckText: TextParagraph[] = [
    {
      text: 'Before you can decommission an S1 site, we need to run an inventory check.',
      lineBreak: false,
    },
  ];

  function goBackHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setScreen(DecommissionASiteScreenType.INITIAL);
    setDecommissionASiteInputs({
      ...decommissionASiteInputs,
      onsiteRadioAnswer: '',
    });
  }

  function decommissionSiteHandler() {
    decommissionReportingData.current.siteDecommissionStartingTimeStamp = new Date();
    sendGenerateDecommissionASitePostRequest();
    setScreen(DecommissionASiteScreenType.DECOMMISSION_IN_PROGRESS);
  }

  const sendSingleGenerateXmlXrinv = () => {
    const request = buildXrinvRequest(decommissionASiteInputs.mpxn);
    inProgress.current = true;
    atomProxyCommsCheckClient
      .generateXmlXrinv(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          inProgress.current = false;
          // TODO
          throw new Error('L1 500 proxy error.');
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (inProgress.current) {
          setTimeout(
            () => sendSingleGetXmlXrinv(l1Response.atomL1response, decommissionASiteInputs.mpxn),
            XrinvXmlInitialTimeout,
          );
        }
      })
      .catch((e) => {
        console.warn('L1 XRINV 500 proxy fail, L2 WONT start', e);
      });
  };

  const sendSingleGetXmlXrinv = (data: any, mpxn: string) => {
    if (!inProgress.current) {
      return;
    }
    const xrinvGetXmlRequest = buildPostRequest(data);
    atomProxyCommsCheckClient
      .getXmlXrinv(xrinvGetXmlRequest)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current && XrinvMaxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), XrinvXmlTimeout);
            throw new Error('L2 XRINV 500 proxy error, retrying');
          } else {
            inProgress.current = false;
            // TODO
            throw new Error('L2 XRINV 500 proxy error, polling will end');
          }
        }
        return httpResponse.json();
      })
      .then((l2Response) => {
        if (l2Response.bolQueryState === 'FAIL') {
          setInventoryData({
            address: '',
            data: [],
            state: 'FAIL',
          });
          inventoryReportingData.current.state = 'FAIL';
          reportInventoryCheck();
          setScreen(DecommissionASiteScreenType.DEVICES_LIST);
        }

        if (l2Response.bolQueryState === 'PROCESSING') {
          if (inProgress.current && XrinvMaxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), XrinvXmlTimeout);
          } else {
            setInventoryData({
              address: getAddress(l2Response),
              data: getXrinvData(l2Response) || [],
              state: 'PROCESSING',
            });
            inventoryReportingData.current.state = 'PROCESSING';
            reportInventoryCheck();
            setScreen(DecommissionASiteScreenType.DEVICES_LIST);
          }
        }

        if (l2Response.bolQueryState === 'SUCCESS') {
          const inventoryResponseData: XrinvData[] = getXrinvData(l2Response) || [];
          setInventoryData({
            address: getAddress(l2Response),
            data: inventoryResponseData,
            state: 'SUCCESS',
          });
          inventoryReportingData.current.state = 'SUCCESS';
          reportInventoryCheck();
          setScreen(DecommissionASiteScreenType.DEVICES_LIST);

          const chf = inventoryResponseData.find((device) => device.deviceType === 'CHF');
          setDecommissionASiteInputs({
            ...decommissionASiteInputs,
            chfDeviceId: chf?.deviceId ?? '',
          });
        }
      })
      .catch((e) => {
        console.warn(e);
      });
  };

  function getAddress(l2Response: any): string {
    return (
      l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice[
        '0'
      ]?.PropertyFilter?.AddressIdentifier?.replace(/,+/g, ' ') ?? ''
    );
  }

  function getXrinvData(l2Response: any): XrinvData[] {
    return l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice?.map((device: any) => ({
      deviceId: device.DeviceID,
      deviceType: device.DeviceType,
      deviceStatus: device.DeviceStatus ? device.DeviceStatus : null,
      mpxn: device.ImportMPXN,
      smetsDeviceType: parseSmetsVersion(device.DeviceGBCSVersion),
    }));
  }

  async function startChatHandler() {
    const buildInitialChatMessage = buildChatData(inventoryData, decommissionASiteInputs.mpxn, S1_FULL_SITE_DECOMMISSION);
    await startChatSession({ message: buildInitialChatMessage, mpxn: decommissionASiteInputs.mpxn, chatReason });
  }

  function sendGenerateDecommissionASitePostRequest() {
    const chfDeviceId: string = decommissionASiteInputs.chfDeviceId;
    if (chfDeviceId) {
      const request = buildPostRequest({
        chfDeviceId: decommissionASiteInputs.chfDeviceId,
      });

      atomProxyDecommissionClient
        .s1SiteGenerateXml(request)
        .then(async (res) => {
          const httpStatusCode = res.status;
          const resBody = await res.json();
          return { httpStatusCode, resBody };
        })
        .then(({ httpStatusCode, resBody }) => {
          if (httpStatusCode === 200) {
            const reqIdentifier = resBody.runId;

            if (reqIdentifier) {
              setTimeout(
                () => sendGetXmlDecommissionASitePostRequest(buildPostRequest({ runId: reqIdentifier })),
                decommXmlInitialTimeout,
              );
            }
          } else {
            setDecommissionSiteResult({
              ...decommissionSiteResult,
              status: DecommissionASiteResultStatus.FAIL,
            });
            setScreen(DecommissionASiteScreenType.DECOMMISSION_RESULT);
          }
        });
    }
  }

  function sendGetXmlDecommissionASitePostRequest(request: any) {
    atomProxyDecommissionClient
      .s1SiteGetXml(request)
      .then(async (res) => {
        const httpStatusCode = res.status;
        const resBody = await res.json();
        return { httpStatusCode, resBody };
      })
      .then(({ httpStatusCode, resBody }) => {
        decommissionRetries += 1;
        if (httpStatusCode === 200) {
          if (resBody.status === 'SUCCESS') {
            setDecommissionSiteResult({
              status: DecommissionASiteResultStatus.SUCCESS,
              chfDeviceId: resBody.chfDeviceId,
              esmeDeviceId: resBody.esmeDeviceId,
              gsmeDeviceId: resBody.gsmeDeviceId,
            });
            decommissionReportingData.current.state = 'SUCCESS';
            decommissionReportingData.current.chfDeviceId = resBody.chfDeviceId;
            reportSiteDecommission();
            setScreen(DecommissionASiteScreenType.DECOMMISSION_RESULT);
          } else if (resBody.status === 'FAIL') {
            setDecommissionSiteResult({
              ...decommissionSiteResult,
              status: DecommissionASiteResultStatus.FAIL,
            });
            decommissionReportingData.current.state = 'FAIL';
            decommissionReportingData.current.chfDeviceId = resBody.chfDeviceId;
            reportSiteDecommission();
            setScreen(DecommissionASiteScreenType.DECOMMISSION_RESULT);
          } else {
            if (decommissionRetries <= decommMaxRetries) {
              setTimeout(() => sendGetXmlDecommissionASitePostRequest(request), decommXmlTimeout);
            } else {
              // Timeout on decommission
              setDecommissionSiteResult({
                ...decommissionSiteResult,
                status: DecommissionASiteResultStatus.TIMEOUT,
              });
              decommissionReportingData.current.state = 'TIMEOUT';
              decommissionReportingData.current.chfDeviceId = resBody.chfDeviceId;
              reportSiteDecommission();
              setScreen(DecommissionASiteScreenType.DECOMMISSION_RESULT);
            }
          }
        } else {
          setDecommissionSiteResult({
            ...decommissionSiteResult,
            status: DecommissionASiteResultStatus.FAIL,
          });
          decommissionReportingData.current.state = 'FAIL';
          decommissionReportingData.current.chfDeviceId = resBody.chfDeviceId;
          setScreen(DecommissionASiteScreenType.DECOMMISSION_RESULT);
        }
      });
  }

  async function reportInventoryCheck() {
    const inventoryCheckResultTimeStamp = new Date();
    const userDetails = await getAuthTokenOrLogOut();
    const requestBody: InventoryCheckReportingRequest = {
      decommissionJourneyId: inventoryReportingData.current.decommissionJourneyId,
      mpxn: decommissionASiteInputs.mpxn,
      contactReason: chatReason,
      engineerEmail: userDetails.userEmail ?? '',
      inventoryCheckResultStatus: inventoryReportingData.current.state,
      inventoryCheckStartingTimeStamp: inventoryReportingData.current.inventoryCheckStartingTimeStamp.toISOString(),
      inventoryCheckResultTimeStamp: inventoryCheckResultTimeStamp.toISOString(),
      inventoryCheckTimeTaken:
        inventoryCheckResultTimeStamp.getTime() - inventoryReportingData.current.inventoryCheckStartingTimeStamp.getTime(),
    };

    // TODO: Report to sentry if any error
    reportingClient.inventoryCheck(requestBody).then();
  }

  async function reportSiteDecommission() {
    const decommissionResultTimeStamp = new Date();
    const requestBody: SiteDecommissionReportingRequest = {
      decommissionJourneyId: inventoryReportingData.current.decommissionJourneyId,
      chfId: decommissionReportingData.current.chfDeviceId,
      engineerOnsite: reportingUtils.mapOnsiteRadioAnswerToEngineerOnSite(decommissionASiteInputs.onsiteRadioAnswer),
      decommissionResultStatus: reportingUtils.mapResultStateToSiteDecommissionResultStatus(
        decommissionReportingData.current.state,
      ),
      decommissionStartingTimeStamp: decommissionReportingData.current.siteDecommissionStartingTimeStamp.toISOString(),
      decommissionResultTimeStamp: decommissionResultTimeStamp.toISOString(),
      decommissionTimeTaken:
        decommissionResultTimeStamp.getTime() - decommissionReportingData.current.siteDecommissionStartingTimeStamp.getTime(),
    };

    // TODO: Report to sentry if any error
    reportingClient.siteDecommission(requestBody).then();
  }

  return (
    <DecommissionASiteScreenContext.Provider value={{ screen, setScreen }}>
      {screen === DecommissionASiteScreenType.INITIAL && (
        <>
          <RadioInputQuestion
            question={onsiteRadioQuestion}
            answers={onsiteRadioAnswers}
            onChangeHandler={onsiteRadioQuestionHandler}
            showError={showError}
            errorMessage={onsiteErrorMessage}
            defaultValue={decommissionASiteInputs.onsiteRadioAnswer}
          />
          {decommissionASiteInputs.onsiteRadioAnswer !== 'Yes' && (
            <InformationPanel
              textArray={onsiteReminderPanelText}
              title={null}
              background={true}
              dataTestId={'info-panel-decomm-onsite-reminder'}
            />
          )}
          {decommissionASiteInputs.onsiteRadioAnswer === 'Yes' && (
            <>
              <TextInput
                label={'Enter an MPxN to retrieve the list of devices'}
                inputName="mpxn"
                inputValue={decommissionASiteInputs.mpxn}
                showError={showMpxnError}
                errorMessage={mpxnErrorMessage}
                onChangeHandler={mpxnTextInputOnChangeHandler}
              />
              {canDecommission() && <InformationPanel title={null} textArray={readyToStartCommsCheckText} background={false} />}
            </>
          )}
          <ButtonContainer>
            <Button
              buttonId="start-comms-check-decommission"
              buttonText="Check devices"
              buttonTheme={ButtonTheme.PRIMARY}
              isButtonEnabled={canDecommission()}
              onClickHandler={checkDevicesHandler}
            />
          </ButtonContainer>
        </>
      )}
      {screen === DecommissionASiteScreenType.CHECK_DEVICES && (
        <>
          <LoadingScreen />
          <ButtonContainer>
            <Button
              buttonId={'go-back-decommission'}
              buttonText={'Go Back'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={goBackHandler}
            />
          </ButtonContainer>
        </>
      )}
      {screen === DecommissionASiteScreenType.DEVICES_LIST && (
        <DecommissionASiteSummary inventoryData={inventoryData} decommissionHandler={decommissionSiteHandler} />
      )}
      {screen === DecommissionASiteScreenType.DECOMMISSION_IN_PROGRESS && (
        <>
          <DecommissionASiteInProgressScreen />
        </>
      )}
      {screen === DecommissionASiteScreenType.DECOMMISSION_RESULT && (
        <>
          <DecommissionASiteResultScreen decommissionSiteResult={decommissionSiteResult} />
          <ButtonContainer>
            {!isResultSuccess && (
              <Button
                buttonId={'start-chat-decommission-a-site'}
                buttonText={'Start Chat'}
                buttonTheme={ButtonTheme.PRIMARY}
                onClickHandler={startChatHandler}
              />
            )}
            <Button
              buttonId={'go-back-decommission-a-site'}
              buttonText={'Finish'}
              buttonTheme={isResultSuccess ? ButtonTheme.PRIMARY : ButtonTheme.SECONDARY}
              onClickHandler={goBackHandler}
            />
          </ButtonContainer>
        </>
      )}
    </DecommissionASiteScreenContext.Provider>
  );
}
